import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request.proxy.service';
import { LabDipRequest } from '../../../models/lab-dip/lab-dip-request';
import ObjectId from 'bson-objectid';
import { FormArray, FormControl, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { Color } from '../../../models/lab-dip/color';
import { BreadcrumbService, EnumValues, ModalService } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { ColorLabDipsManagementModalComponent } from '../../modals/color-lab-dips-management-modal/color-lab-dips-management-modal.component';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { FabricCategory } from '../../../models/fabric/fabricCategory';
import { YarnMaterial } from '../../../models/yarns/yarn-material';
import { UserLite } from '../../../models/user-master/user-lite';
import { EnumService } from '../../../services/enum.service';
import { Customer } from '../../../models/finance/customer';
import { Brand } from '../../../models/finance/brand';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, map, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { ExistingColorMatch } from '../../../models/lab-dip/existing-color-match';
import { Shade } from '../../../models/shade/shade';
import { ShadeProxyService } from '../../../proxies/item/shade.proxy.service';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { Fabric } from '../../../models/fabric/fabric';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { BusinessEntityType } from '../../../models/finance/business';
import { UserMaster } from '../../../models/user-master/user-master';
import { UserMasterProxyService } from '../../../proxies/user-master/user-master.proxy.service';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';

import { Translation } from '../../../models/Translations';
import { TranslateService } from '@ngx-translate/core';
import { LapDipRequestRoles } from '../../../models/authorization/enum/user-group.enum';
import _ from 'lodash';
@Component({
  selector: 'app-lab-dip-request',
  templateUrl: './lab-dip-request-details.component.html',
  styleUrls: ['./lab-dip-request-details.component.scss'],
})
export class LabDipRequestDetailsComponent implements OnInit {
  allBrands: Brand[] = [];
  allCustomers: Customer[] = [];
  formGroupSubmitted = false;

  labDipRequest: LabDipRequest | null = null;
  isLoading = 0;
  isEditing = false;
  user: UserLite | null = null;
  createdByUserProfile: UserMaster | null = null;
  createdByDisplayName = '';
  private userCache: Map<string, UserMaster> = new Map();

  dateCreated: FormControl<string | null> = new FormControl(new Date().toISOString().split('T')[0]);
  requestDate: FormControl<string | null> = new FormControl(new Date().toISOString().split('T')[0]);

  // fabricPrefix (this comes from distinct first sections of fabric category codes)
  illumination = new FormControl<number | null>(null);
  brandReference = new FormControl<Brand | null>(null);
  customerReference = new FormControl<Customer | null>(null, Validators.required);
  isForProductDevelopment = new FormControl<boolean>(false);
  fabrics: Fabric[] = [];
  fabricCodeReference = new FormControl<Fabric | null>(null);
  // Colors (kept largely the same)
  colors = new FormArray<FormGroup>([]);

  // Build overall form group
  formGroup = new FormGroup({
    requestDate: this.requestDate,
    brandReference: this.brandReference,
    customerReference: this.customerReference,
    illumination: this.illumination,
    isForProductDevelopment: this.isForProductDevelopment,
    colors: this.colors,
  });

  // For dropdowns
  yarnMaterials: YarnMaterial[] = [];
  fabricCategories: FabricCategory[] = [];
  fabricPrefixes: string[] = [];

  /**
   * Per color row: other requests of this customer that already have the same color name on a shared
   * fabric code. Warning only, never blocks the save (ADO #5876, agreed with the SAC team).
   */
  private readonly existingColorMatches = new Map<AbstractControl, ExistingColorMatch[]>();
  /** Emits when customer or fabric codes change so every color row re-checks its warning. */
  private readonly existingColorRefresh$ = new Subject<void>();
  // Enums
  illuminationTypes: EnumValues;
  dyeingTypes: EnumValues;
  statusTypes: EnumValues;
  statusTypesOptions: { label: string; value: number }[] = [];
  illuminationTypesOptions: { label: string; value: number }[] = [];
  dyeingTypesOptions: { label: string; value: number }[] = [];
  standardTypesOptions: { label: string; value: number }[] = [];

  shadeOptions: { label: string; value: string }[] = [];
  allShades: Shade[] = [];
  allFabric: Fabric[] = [];
  translations: Record<string, Translation> = {
    Error_Form_Invalid: {
      es: 'El formulario tiene campos inválidos. Por favor, complete todos los campos requeridos',
      en: 'Form has invalid fields. Please fill all required fields',
    },
  };
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private shadeProxyService: ShadeProxyService,
    private fabricProxyService: FabricProxyService,
    private labDipRequestProxyService: LabDipRequestProxyService,
    private modalService: ModalService,
    private userAuthenticationService: UserAuthenticationService,
    private getEnumService: EnumService,
    private businessProxyService: BusinessProxyService,
    private userMasterProxyService: UserMasterProxyService,
    private translateService: TranslateService,
    private breadcrumbService: BreadcrumbService,
  ) {
    // set Enums
    this.illuminationTypes = this.getEnumService.getEnum('Illumination', 'itemEnums').values;
    this.dyeingTypes = this.getEnumService.getEnum('DyeingType', 'itemEnums').values;
    this.statusTypes = this.getEnumService.getEnum('Status', 'itemEnums').values;
    this.illuminationTypesOptions = this._setOptionsForPrimeNg(this.illuminationTypes);
    this.statusTypesOptions = this._setOptionsForPrimeNg(this.statusTypes);
    this.dyeingTypesOptions = this._setOptionsForPrimeNg(this.dyeingTypes);
  }

  canEdit() {
    // if Admin
    if (AuthService.hasRole(LapDipRequestRoles.LabDipRequestAdmin) || AuthService.hasRole(LapDipRequestRoles.LabDipRequestEditor)) {
      return true;
    }
    // if Viewer
    if (AuthService.hasRole(LapDipRequestRoles.LabDipRequestViewer)) {
      return false;
    }
    // Default
    return false;
  }

  removeFabric(fabricCode: string) {
    this.fabrics = this.fabrics.filter((f) => f.fabricCode !== fabricCode);
    this.refreshExistingColorMatches();
  }

  canRemoveFabric(fabricCode: string): boolean {
    if (!this.labDipRequest) {
      return true; // If no lab dip request, allow removal
    }
    return !this.labDipRequest.fabricCodes.includes(fabricCode);
  }

  ngOnInit() {
    // Load dropdown data
    this.user = this.userAuthenticationService.userLite;
    if (this.user?.username) {
      this.loadUserProfile(this.user.username);
    }
    console.log('User:', this.user);

    this.fabricCodeReference.valueChanges.subscribe((value) => {
      if (!_.isNil(value?.id)) {
        this.fabricCodeReference.setValue(null, { emitEvent: false });
        if (this.fabrics.some((f) => f.fabricCode === value.fabricCode)) {
          GlobalMessageUtil.error('Error', this.translateService.instant('LabDipRequests_FabricCode_Already_Added'));
          return;
        }
        this.fabrics.push(value);
        this.refreshExistingColorMatches();
      }
    });

    this.customerReference.valueChanges.subscribe(() => this.refreshExistingColorMatches());

    forkJoin({
      businesses: this.businessProxyService.search({
        businessTypes: [BusinessEntityType.Brand, BusinessEntityType.Client],
      }),
      shades: this.shadeProxyService.getAllShades(),
      fabrics: this.fabricProxyService.getAllFabrics(),
    }).subscribe({
      next: ({ businesses, shades, fabrics }) => {
        this.allBrands = businesses.data?.items?.filter((b) => b.businessTypes?.includes(BusinessEntityType.Brand)) ?? [];
        this.allBrands = this.allBrands.map((brand) => {
          return {
            ...brand,
            fieldToDisplay: `(${brand.code}) ${brand.name}`,
          };
        });
        this.allCustomers = businesses.data?.items?.filter((b) => b.businessTypes?.includes(BusinessEntityType.Client)) ?? [];
        this.allCustomers = this.allCustomers.map((customer) => {
          return {
            ...customer,
            fieldToDisplay: `(${customer.code}) ${customer.name}`,
          };
        });
        this.allFabric = fabrics.data ?? [];
        this.allFabric = this.allFabric.map((fabric) => {
          return {
            ...fabric,
            fieldToDisplay: `(${fabric.fabricCode}) ${fabric.longDescription}`,
          };
        });
        this.allShades = shades.data ?? [];
        this.shadeOptions = this._convertShadesToOptions(this.allShades);
        this.route.queryParams.subscribe((params) => {
          if (params['id']) {
            if (params['id'] !== 'new') {
              this.isLoading++;
              this.isEditing = true;
              this.labDipRequestProxyService.getLabDipRequest(params['id']).subscribe({
                next: (response) => {
                  if (!response) {
                    this.router.navigate(['/lab-dip-requests']);
                  } else {
                    this.labDipRequest = response.data;
                    this.breadcrumbService.setCurrent(this.labDipRequest?.requestNumber);
                    this.populateForm();
                    if (this.labDipRequest?.createdBy) {
                      this.loadUserProfile(this.labDipRequest.createdBy);
                    }
                    this.brandReference.setValue(this.allBrands.find((brand) => brand.id === this.labDipRequest?.brandReference) || null, {
                      emitEvent: false,
                    });

                    this.customerReference.setValue(
                      this.allCustomers.find((customer) => customer.id === this.labDipRequest?.customerReference) || null,
                      {
                        emitEvent: false,
                      },
                    );
                    this.fabrics = this.allFabric.filter((f) => this.labDipRequest?.fabricCodes.includes(f.fabricCode!));
                    this.refreshExistingColorMatches();
                  }
                  this.isLoading--;
                },
                error: () => {
                  this.isLoading--;
                  this.router.navigate(['/lab-dip-requests']);
                },
              });
            } else {
              this.isEditing = false;
              this.breadcrumbService.setCurrent('Common_New');
              const today = new Date().toISOString().split('T')[0];
              this.requestDate.setValue(today);
              if (params['clientReference'] && params['fabricCode']) {
                this.customerReference.setValue(this.allCustomers.find((customer) => customer.id === params['clientReference']) || null);
                this.fabricCodeReference.setValue(this.allFabric.find((fabric) => fabric.fabricCode === params['fabricCode']) || null);
                this.brandReference.setValue(this.allBrands.find((brand) => brand.id === params['brandReference']) || null);
              }
            }
          } else {
            this.router.navigate(['/lab-dip-requests']);
          }
        });
      },
      error: (e) => {
        console.error('Error fetching processes and process types', e);
      },
    });

    //Disable form by Rol
    if (!this.canEdit()) {
      this.dateCreated.disable();
      this.requestDate.disable();
      this.illumination.disable();
      this.brandReference.disable();
      this.customerReference.disable();
      this.fabricCodeReference.disable();
    }
  }

  populateForm() {
    if (this.labDipRequest) {
      // Format requestDate for input type="date"
      const reqDate = new Date(this.labDipRequest.requestDate).toISOString().split('T')[0];
      this.requestDate.setValue(reqDate);
      //
      // Map the model's yarnPrefix to the fabricPrefix control
      // Illumination
      this.illumination.setValue(this.labDipRequest.illumination ?? null);
      // IsForProductDevelopment - set value and disable when editing
      this.isForProductDevelopment.setValue(this.labDipRequest.isForProductDevelopment ?? false);
      this.isForProductDevelopment.disable();
      // Populate colors
      if (this.labDipRequest.colors && this.labDipRequest.colors.length) {
        this.labDipRequest.colors.forEach((color: Color) => {
          const colorGroup = this.createColorGroup(color);
          if (!this.canEdit()) {
            colorGroup.disable();
          }
          this.colors.push(colorGroup);
        });
      }
    }
  }

  createColorGroup(color?: Color): FormGroup {
    const isOldLabDip = new FormControl(color?.isOldLabDip || false);
    const customLabDipNumber = new FormControl(color?.customLabDipNumber || '', [this.labDipNumberFormatValidator.bind(this)]);

    if (!color?.isOldLabDip) {
      customLabDipNumber.disable();
    }

    isOldLabDip.valueChanges.subscribe((value) => {
      if (value) {
        customLabDipNumber.enable();
      } else {
        customLabDipNumber.disable();
        customLabDipNumber.setValue('');
      }
    });

    const name = new FormControl(color?.name || '', [Validators.required, this.duplicateColorValidator.bind(this)]);
    const group = new FormGroup({
      id: new FormControl(color?.id || null),
      name,
      status: new FormControl(color?.status),
      dyeingType: new FormControl(color?.dyeingType),
      approvedRecipeNumber: new FormControl(color?.approvedRecipeNumber || ''),
      // New fields for Standard
      dyeingColorStandardCode: new FormControl(color?.dyeingColorStandardCode || null),
      pantone: new FormControl(color?.pantone || ''),
      sampleRecipeNumber: new FormControl(color?.sampleRecipeNumber || ''),
      isOldLabDip: isOldLabDip,
      customLabDipNumber: customLabDipNumber,
    });

    // Re-check the "already exists" warning when the name, the customer or the fabric codes change.
    merge(name.valueChanges, this.existingColorRefresh$)
      .pipe(
        debounceTime(1000),
        map(() => this.buildExistingColorLookupKey(name.value)),
        distinctUntilChanged(),
        switchMap(() => this.lookupExistingColorMatches(this.normalizeColorName(name.value))),
      )
      .subscribe((matches) => this.existingColorMatches.set(group, matches));

    return group;
  }

  /**
   * Trims and collapses whitespace, lower-cases, so "White", "White   " and "white"
   * are compared as the same color name (mirrors the API normalization, ADO #5876).
   */
  private normalizeColorName(name: unknown): string {
    return typeof name === 'string' ? name.trim().replace(/\s+/g, ' ').toLowerCase() : '';
  }

  private buildExistingColorLookupKey(name: unknown): string {
    const fabricCodes = this.fabrics
      .map((f) => f.fabricCode ?? '')
      .sort()
      .join(',');
    return `${this.customerReference.value?.id ?? ''}|${fabricCodes}|${this.normalizeColorName(name)}`;
  }

  private lookupExistingColorMatches(colorName: string): Observable<ExistingColorMatch[]> {
    const customerId = this.customerReference.value?.id;
    const fabricCodes = this.fabrics.map((f) => f.fabricCode).filter((code): code is string => !!code);
    if (!colorName || !customerId || fabricCodes.length === 0) {
      return of([]);
    }
    return this.labDipRequestProxyService.findExistingColorMatches(customerId, colorName, fabricCodes, this.labDipRequest?.id).pipe(
      map((response) => response.data ?? []),
      catchError(() => of([])),
    );
  }

  refreshExistingColorMatches(): void {
    this.existingColorRefresh$.next();
  }

  getExistingColorWarning(color: AbstractControl): string | null {
    const matches = this.existingColorMatches.get(color) ?? [];
    if (matches.length === 0) {
      return null;
    }
    const requests = matches.map((m) => `${m.requestNumber ?? m.labDipRequestId} (${m.fabricCodes.join(', ')})`).join(', ');
    return this.translateService.instant('LabDipRequests_ColorExistsWarning').replace('{{requests}}', requests);
  }

  duplicateColorValidator(control: AbstractControl) {
    const currentName = this.normalizeColorName(control.value);
    if (currentName === '') {
      return null;
    }

    const colorNames = this.colors.controls.map((ctrl) => this.normalizeColorName(ctrl.get('name')?.value));
    const occurrences = colorNames.filter((name) => name === currentName).length;
    if (occurrences > 1) {
      return {
        duplicateColor: this.translateService.instant('LabDipRequests_DuplicateColorName'),
      };
    }
    return null;
  }

  labDipNumberFormatValidator(control: AbstractControl) {
    const value = control.value;

    if (!value || value === '') {
      return null;
    }

    const labDipPattern = /^LD-\d{11}$/;

    if (!labDipPattern.test(value)) {
      return {
        invalidLabDipFormat:
          this.translateService.currentLang === 'es'
            ? "El número de Lab Dip tiene un formato inválido. Debe ser 'LD-' seguido de exactamente 11 dígitos (ej. LD-20251000001)"
            : "Lab Dip Number has an invalid format. It must be 'LD-' followed by exactly 11 digits (e.g., LD-20251000001)",
      };
    }

    return null;
  }

  addColor() {
    this.colors.push(this.createColorGroup());
  }

  removeColor(index: number) {
    this.existingColorMatches.delete(this.colors.at(index));
    this.colors.removeAt(index);
  }

  openColorLabDipsManagementModal(index: number) {
    const color = this.colors.controls[index];
    this.modalService.open(
      ColorLabDipsManagementModalComponent,
      {},
      {
        colorId: color.value.id,
        colorName: color.value.name,
        labDipRequestId: this.labDipRequest?.id,
        labDipIds: color.controls['labDipIds'],
      },
    );
  }

  redirectToLabDipDetails(id: string) {
    const url = this.router.createUrlTree(['/lab-dip-details'], { queryParams: { id } }).toString();
    window.open(url, '_blank');
  }

  onSubmit() {
    this.formGroupSubmitted = true;
    if (!this.validateColors()) {
      GlobalMessageUtil.error('Validation Error', 'At least one color is required for the lab dip request.');
      return;
    }

    this.colors.controls.forEach((colorControl) => {
      colorControl.markAllAsTouched();
    });

    if (!this.validateColorNames()) {
      GlobalMessageUtil.error('Validation Error', 'All colors must have a name');
      return;
    }

    if (this.colors.invalid) {
      GlobalMessageUtil.error('Validation Error', 'Please ensure all colors are valid and have names.');
      return;
    }

    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      GlobalMessageUtil.error(
        'Validation Error',
        this.translations['Error_Form_Invalid'][this.translateService.currentLang as keyof Translation],
      );
      return;
    }

    if (this.formGroup.valid) {
      const currentUser = this.userAuthenticationService.userLite;
      const labDipRequest: LabDipRequest = {
        id: this.isEditing ? this.labDipRequest?.id : new ObjectId().toHexString(),
        requestNumber: this.isEditing ? this.labDipRequest!.requestNumber : undefined,
        requestDate: this.requestDate.value ? new Date(this.requestDate.value) : new Date(),
        brandReference: this.brandReference.value?.id ?? '',
        customerReference: this.customerReference.value?.id ?? '',
        fabricCodes: this.fabrics.map((fabric) => fabric.fabricCode!),
        illumination: this.illumination.value!,
        colors: this.colors.value,
        isForProductDevelopment: this.isForProductDevelopment.value ?? false,
        createdBy: this.isEditing ? this.labDipRequest!.createdBy! : currentUser!.username,
        modifiedBy: this.isEditing ? currentUser?.username : undefined,
        modifiedDate: this.isEditing ? new Date() : undefined,
      };

      this.isLoading++;
      this.labDipRequestProxyService.upsertLabDipRequest(labDipRequest).subscribe({
        next: (result) => {
          this.isLoading--;
          GlobalMessageUtil.success(
            'Lab Dip Request Saved',
            this.translateService.instant('LabDipRequests_Saved_Successfully').replace('{{name}}', result.data?.requestNumber ?? 'patata'),
          );

          this.router.navigate(['/lab-dip-requests']);
        },
        error: () => {
          this.isLoading--;
        },
      });
    }
  }

  private _setOptionsForPrimeNg(value: EnumValues) {
    return Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
  }
  private _convertShadesToOptions(shades: Shade[]): { label: string; value: string }[] {
    return shades.map((shade) => ({
      label: shade.name,
      value: shade.id || '',
    }));
  }

  cancel() {
    this.router.navigate(['/lab-dip-requests']);
  }

  brandTypeTypeahead = (term: string): Observable<Brand[]> => {
    const filtered = this.allBrands.filter((brand) => brand!.name!.toLowerCase().includes(term.toLowerCase()));
    return of(filtered);
  };

  customerTypeTypeahead = (term: string): Observable<Customer[]> => {
    const filtered = this.allCustomers.filter((customer) => customer!.name!.toLowerCase().includes(term.toLowerCase()));
    return of(filtered);
  };

  fabricCategoryTypeahead = (term: string): Observable<Fabric[]> => {
    const filtered = this.allFabric
      .filter(
        (fabric) =>
          fabric!.fabricCode!.toLowerCase().includes(term.toLowerCase()) ||
          fabric!.longDescription!.toLowerCase().includes(term.toLowerCase()),
      )
      .map((fabric) => {
        return {
          ...fabric,
          fieldToDisplay: `${fabric.fabricCode} - ${fabric.longDescription}`,
        };
      });
    return of(filtered);
  };

  validateColors(): boolean {
    return this.colors.length > 0;
  }

  validateColorNames(): boolean {
    return this.colors.controls.every((colorControl) => {
      const nameControl = colorControl.get('name');
      return nameControl?.value && nameControl.value.trim() !== '';
    });
  }

  loadUserProfile(username: string): void {
    if (!username) {
      this.createdByDisplayName = 'Unknown User';
      return;
    }

    // Check cache first
    if (this.userCache.has(username)) {
      this.createdByUserProfile = this.userCache.get(username)!;
      this.setDisplayName();
      return;
    }

    this.userMasterProxyService.getUserByUsername(username).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.createdByUserProfile = response.data;

          // Cache the user
          this.userCache.set(username, this.createdByUserProfile);

          this.setDisplayName();
        } else {
          this.createdByDisplayName = username;
        }
      },
      error: (error) => {
        console.error('Error loading user profile:', error);
        this.createdByDisplayName = username;
      },
    });
  }
  private setDisplayName(): void {
    if (this.createdByUserProfile?.profile) {
      const profile = this.createdByUserProfile.profile;
      const nameParts = [profile.firstName, profile.middleName, profile.lastName, profile.secondLastName].filter(
        (part) => part && part.trim() !== '',
      );

      this.createdByDisplayName = nameParts.join(' ') || this.createdByUserProfile.username;
    } else {
      this.createdByDisplayName = this.createdByUserProfile?.username || 'Unknown User';
    }
  }

  public get colorStatusOptions(): { label: string; value: number }[] | [] {
    if (!this.statusTypesOptions || typeof this.statusTypesOptions !== 'object') return [];

    if (AuthService.hasRole(LapDipRequestRoles.LabDipRequestEditor) && !AuthService.hasRole(LapDipRequestRoles.LabDipRequestAdmin)) {
      return this.statusTypesOptions.filter((s) => s.value == 0);
    }

    if (this.canEdit()) {
      return this.statusTypesOptions;
    }

    return [];
  }
}
